import { Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import {
  acceptedImageFormats,
  imageExtensions,
} from '@texas/utils/helpers/filesHelper';
import { useTranslation } from 'react-i18next';
import { FileDropzone } from '../../../../shared/dropzone/FileDropzone';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useCallback, useEffect } from 'react';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { ServerError } from '@texas/types';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { request } from '@texas/utils/helpers/httpHelpers';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { FileBrowserModal } from '../../../../shared/files/FileBrowserModal';
import { ImageDropzone } from '../../../../shared/dropzone/ImageDropzone';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { CaptureSessionType } from '@texas/api/endpoints/capture/captureApi';
import { variantPhotoEvents } from './variantPhotoEvents';

interface Props {
  variantId: number;
  allowEdit: boolean;
  folderId: number;
  onPhotoFetched?: (fileId: number | null) => void;
}

export function Photo({
  variantId,
  folderId,
  allowEdit,
  onPhotoFetched,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request: updateRequest } = useApiRequest(variantsApi.updatePhoto);

  const { data: photoInfo, refetch: refetchPhoto } = useApiResource(
    variantsApi.getVariantPhoto,
  );

  useEffect(() => {
    refetchPhoto(variantId);
  }, [refetchPhoto, variantId]);

  useEffect(() => {
    onPhotoFetched?.(photoInfo?.id ?? null);
  }, [photoInfo, onPhotoFetched]);

  useEffect(() => {
    const unsub = variantPhotoEvents.photoUpdated.subscribe((updatedId) => {
      if (updatedId === variantId) {
        refetchPhoto(variantId);
      }
    });
    return () => unsub();
  }, [refetchPhoto, variantId]);

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();

  const {
    isOpen: isFileBrowserOpen,
    onClose: onFileBrowserClose,
    onOpen: onFileBrowserOpen,
  } = useDisclosure();

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [variantId, file.id],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          variantPhotoEvents.photoUpdated.dispatch(variantId);
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [t, toast, updateRequest, variantId],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile, maxSize);

  const photo =
    photoInfo?.id && photoInfo.identifier
      ? {
          id: photoInfo.id,
          identifier: photoInfo.identifier,
          archived: photoInfo.archived,
        }
      : null;

  return (
    <Flex direction="column" gap={2}>
      <Text variant="header">{t('variant.photo')}</Text>
      {photo && (
        <ImageDropzone
          imageId={photo.id}
          imageIdentifier={photo.identifier}
          imageSrc={clientEndpoints.previewArticlePhoto(variantId, photo.id)}
          imageArchived={photo.archived}
          allowEdit={allowEdit}
          isUploading={isUploading}
          uploadFailed={uploadFailed}
          fileUploads={fileUploads}
          removeFileUpload={removeFileUpload}
          onUpload={(files) => uploadFiles(files, folderId)}
          onRemoveFileOpen={onRemoveFileOpen}
          onFileBrowserOpen={onFileBrowserOpen}
          capturePhotoProps={{
            referenceId: variantId,
            referenceType: CaptureSessionType.ArticleVariantPhoto,
            text1: t('variant.photo'),
            onPhotoReceived: (file) => updateFile(file),
          }}
        />
      )}
      {allowEdit && !photo && (
        <FileDropzone
          accept={acceptedImageFormats}
          isUploading={isUploading}
          uploadFailed={uploadFailed}
          fileUploads={fileUploads}
          removeFileUpload={removeFileUpload}
          onUpload={(files) => uploadFiles(files, folderId)}
          onFileBrowserOpen={onFileBrowserOpen}
          capturePhotoProps={{
            referenceId: variantId,
            referenceType: CaptureSessionType.ArticleVariantPhoto,
            text1: t('variant.photo'),
            onPhotoReceived: (file) => updateFile(file),
          }}
        />
      )}
      <VerifyDialogWithRequest
        headerTitle={t('fileBrowser.removeFile')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        primaryRequest={variantsApi.deletePhoto}
        args={[variantId]}
        isOpen={isRemoveFileOpen}
        onClose={onRemoveFileClose}
        onPerformed={() => {
          variantPhotoEvents.photoUpdated.dispatch(variantId);
        }}
        onSuccessTitle={t('fileBrowser.fileRemoved')}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithRequest>
      <FileBrowserModal
        rootFolderId={folderId}
        mode="Select"
        isOpen={isFileBrowserOpen}
        onClose={onFileBrowserClose}
        extensionFilter={imageExtensions}
        onSelect={(file: FileLink) => {
          updateFile(file);
          onFileBrowserClose();
        }}
      />
    </Flex>
  );
}
